import { ROUTES } from '@shared/utils';
import type { useTranslations } from 'next-intl';
import type { ScratchcardDetails } from '../../../../scratchcards/types';
import { resolveScratchcardMappingPath } from '../../../../scratchcards/utils/resolveScratchcardMappingPath';
import type { ContentType } from '../../../../types';
import { isWagerBonusFundOffer, type BonusOffer, type PromoStatus, type WagerBonusFundOffer } from '../../../types/types';

const DEFAULT_BONUS_IMAGE = '/images/bonuses-bg.png';

type WagerBonusFundFields = Pick<WagerBonusFundOffer, 'wageringRequired' | 'currentWageredAmount' | 'maxBet' | 'maxWinnings'>;

type MapBonusArgs = {
  item: BonusOffer;
  t: ReturnType<typeof useTranslations>;
  scratchcards?: ScratchcardDetails[];
  isPrevious?: boolean;
};

/**
 * Maps a bonus status string to a normalised PromoStatus value.
 *
 * @param {string} status - The status string to map (e.g., 'ACTIVE', 'EXPIRED').
 * @returns {PromoStatus} The mapped promo status ('active', 'expired', 'completed', or 'pending').
 */
function mapBonusStatus(status: string): PromoStatus {
  const statusLower = status?.toLowerCase();
  if (statusLower === 'active') return 'active';
  if (['expired', 'deactivated', 'cancelled'].includes(statusLower)) return 'expired';
  if (['completed', 'consumed', 'redeemed'].includes(statusLower)) return 'completed';
  return 'pending';
}
const shouldUseStatus = (status: string) =>
  status === 'EXPIRED' || status === 'COMPLETED' || status === 'CONSUMED' || status === 'REDEEMED' || status === 'DEACTIVATED' || status === 'PENDING';

function getBonusRoute(productIds: string[], productTypes: string[], scratchcards?: ScratchcardDetails[]) {
  const { CASINO, LOTTO, SCRATCHCARDS, SYNDICATES, PRODUCT_BROWSE } = ROUTES;
  const hasProducts = Boolean(productIds?.length);

  if (hasProducts) {
    if (productTypes.includes('GAMES')) return CASINO.GAME(productIds[0]!);
    if (productTypes.includes('LOTTO')) return LOTTO.LOTTERIES[productIds[0]!.toUpperCase() as keyof typeof LOTTO.LOTTERIES];
    if (productTypes.includes('SCRATCHCARDS')) {
      const scratchcardId = productIds[0]!;
      if (scratchcards?.length) {
        const mappingPath = resolveScratchcardMappingPath(scratchcards, scratchcardId);
        return mappingPath ? SCRATCHCARDS.GAME(mappingPath) : SCRATCHCARDS.DISCOVERY;
      }
      return SCRATCHCARDS.DISCOVERY;
    }
  }

  if (productTypes.length > 0 && !hasProducts) {
    if (productTypes.includes('GAMES')) return PRODUCT_BROWSE.GAMES;
    if (productTypes.includes('LOTTO')) return PRODUCT_BROWSE.LOTTERIES;
    if (productTypes.includes('SCRATCHCARDS')) return SCRATCHCARDS.DISCOVERY;
    if (productTypes.includes('BUNDLE')) return SYNDICATES;
    if (productTypes.includes('SPORTSBOOK')) return PRODUCT_BROWSE.SPORTSBOOK;
  }

  return ROUTES.HOME;
}

function hasMultipleGames(bonusProductTypes: string[], gameIds: string[] | undefined): boolean {
  return bonusProductTypes.includes('GAMES') && Boolean(gameIds);
}

function getWagerBonusFundFields(item: BonusOffer): Partial<WagerBonusFundFields> {
  return isWagerBonusFundOffer(item)
    ? {
        wageringRequired: item.wageringRequired,
        currentWageredAmount: item.currentWageredAmount,
        maxBet: item.maxBet,
        maxWinnings: item.maxWinnings,
      }
    : {};
}

/**
 * Maps a BonusOffer item to the bonus fund promo content data structure for display.
 *
 * @param {BonusOffer} args.item - The bonus offer item to map.
 * @param {ReturnType<typeof useTranslations>} args.t - The translation function.
 * @returns {object} The mapped bonus data for use in promo content components.
 */
function mapBonus({ item, t, scratchcards, isPrevious }: MapBonusArgs) {
  const {
    id,
    reason,
    status,
    activationDate,
    expireDate,
    bundleCode,
    iterationNr,
    amount,
    currentAmount,
    ctaStaticKey,
    pageMappingId,
    bonusProductTypes = [],
    productIds,
    gameIds,
    image,
  } = item;
  const { wageringRequired, currentWageredAmount, maxBet, maxWinnings } = getWagerBonusFundFields(item);
  const balance = currentAmount?.fixed_amount ?? 0;
  const required = wageringRequired?.fixed_amount ?? 0;
  const current = currentWageredAmount?.fixed_amount ?? 0;

  const mappedStatus = mapBonusStatus(status);

  const data: Partial<ContentType> = {
    id,
    amount,
    title: reason,
    cta: t('cta.from_status.bonuses', { ctaStaticKey: shouldUseStatus(status) ? status.toLowerCase() : ctaStaticKey }),
    moreInfoCta: t('cta.more_info'),
    moreInfoCtaModal: true,
    expirationDate: expireDate,
    activationDate,
    status: mappedStatus,
    bundleCode,
    iterationNr,
    image: { src: image?.link || DEFAULT_BONUS_IMAGE, alt: image?.altText ?? '' },
    progress: {
      balance,
      progress: required > 0 ? { current, max: required, labelFormat: 'currentOfMax', isCurrency: true } : undefined,
    },
    maxBet,
    maxWinnings,
    bonusBalanceText: t('bonus_balance'),
    bonusProgressText: t('wagering_progress'),
    validOn: t(`validOn.${bonusProductTypes.length > 0 ? bonusProductTypes[0] : 'ALL'}`),
  };

  const shouldNavigate = mappedStatus !== 'pending' && pageMappingId && !hasMultipleGames(bonusProductTypes, gameIds);
  const shouldUseClaiming = mappedStatus === 'pending';
  const shouldShowGamesList = mappedStatus === 'active' && hasMultipleGames(bonusProductTypes, gameIds);

  if (shouldNavigate && !shouldUseClaiming) {
    // If the bonus is previous, never allow navigation
    data.ctaHref = isPrevious ? undefined : getBonusRoute(productIds, bonusProductTypes, scratchcards);
  }

  if (shouldShowGamesList) {
    data.productIds = gameIds;
    data.bonusProductTypes = bonusProductTypes;
  }

  return data;
}

export { DEFAULT_BONUS_IMAGE, mapBonus, mapBonusStatus };
